import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  signal,
  WritableSignal,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { noop, tap } from 'rxjs';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { FieldDef } from 'app/core/models';
import { LayoutService } from 'app/layout/layout.service';
import { Dictionary } from 'app/shared/models/dictionary';
import { isArraysEqual, isEmptyOrWhitespace, keepProps } from 'app/shared/Utils/common.utils';
import {
  CustomField,
  CustomFormField,
  CustomFormFieldType,
  CustomFormSaveAction,
  CustomFormBeforeSaveAction,
} from '../custom-form.types';
import { Permission } from 'app/core/enums';
import { RelatedToType } from 'app/shared/enums';
import { ActivatedRoute } from '@angular/router';
import { EthanAIAbilitiesService } from '@services/ethan-ai-abilities.service';

@Component({
  selector: 'app-custom-form',
  templateUrl: './custom-form.component.html',
  styleUrls: ['./custom-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomFormComponent extends OnDestroyMixin implements OnInit {
  private _selectedFields: CustomField[];
  private _allowedFields: FieldDef[];
  private _relatedToType: RelatedToType;
  private _dataSources: Dictionary<any>;
  private _model: Dictionary<any> = {};
  private get customFormFields(): string[] {
    const fields = [
      'cff_address',
      'template_id',
      'cff_add_properties',
      'tag_list',
      'category_name',
      'address_string',
      'company_name',
    ];

    if (this._relatedToType === RelatedToType.Project) {
      fields.unshift('title');
    } else {
      fields.unshift('name');
    }

    return fields;
  }

  relatedToTypeEnum = RelatedToType;
  setValues: Dictionary<any> = {};
  selectedFieldDef: FieldDef;
  currentFields: CustomFormField[];
  ui: WritableSignal<{
    isEditing: boolean;
    isLoading: boolean;
    isAddingField: boolean;
    isSaving: boolean;
    isAiLoading: boolean;
  }> = signal({ isEditing: false, isLoading: false, isAddingField: false, isSaving: false, isAiLoading: false });

  @Input() set selectedFields(value: CustomField[]) {
    this._selectedFields = value;
    this.currentFields = this._selectedFields.map((selectedField: CustomField) =>
      this.toCustomFormField(selectedField),
    );
  }

  @Input() set allowedFields(value: FieldDef[]) {
    this._allowedFields = value;
  }

  @Input() set dataSources(value: Dictionary<any>) {
    this._dataSources = value;
  }

  @Input() set relatedToType(value: RelatedToType) {
    this._relatedToType = value;
  }

  @Input() initialValue: any;

  @Input() commonData: Dictionary<any>;
  @Input() saveAction: CustomFormSaveAction;
  @Input() beforeSaveAction: CustomFormBeforeSaveAction;
  @Input() enableEditing = true;
  @Input() canEditLayout = true;
  @Input() renderAttachFileField = true;
  @Input() showFormTitle = true;
  @Input() showCancelButtons: { fromHeader: boolean; fromFooter: boolean } = { fromHeader: true, fromFooter: true };
  @Input() enableAiButton: boolean = false;

  get allowedFields(): FieldDef[] {
    return this._allowedFields;
  }

  get dataSources(): Dictionary<any> {
    return this._dataSources;
  }

  get relatedToType(): RelatedToType {
    return this._relatedToType;
  }

  get isSaveButtonDisabled(): boolean {
    return this.isFormInvalid || this.ui().isSaving;
  }

  get selectedFile(): File {
    return this._model.file;
  }

  get canSaveTemplate(): boolean {
    return this.isTemplateDirty && !this.ui().isAddingField;
  }

  get formFieldData(): any {
    return this.commonData.formFieldData;
  }

  private get isTemplateDirty(): boolean {
    return !isArraysEqual(this.normalizeArray(this._selectedFields), this.normalizeArray(this.currentFields));
  }

  private get isFormInvalid(): boolean {
    return !this.currentFields
      .filter(({ isRequired }) => isRequired)
      .every((item) => {
        const value = this._model[item.name];

        if (typeof value === 'string') {
          return !isEmptyOrWhitespace(value);
        }

        return !!value;
      });
  }

  get canCreatePortfolio(): boolean {
    return this._model.properties?.length > 1;
  }

  @Output() onTemplateChanged = new EventEmitter<CustomField[]>();
  @Output() onNavigationByUrlRequested = new EventEmitter<string>();

  constructor(
    private readonly layoutService: LayoutService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly ethanAbilities: EthanAIAbilitiesService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.relatedToType === RelatedToType.Project) {
      let selectedPipeline = this.commonData?.pipelineId;
      const staticPipeline = this.formFieldData?.options?.pipelines.find(
        (option) => option.id === selectedPipeline && option.pipeline_type === 'static',
      );

      if (!staticPipeline) {
        selectedPipeline = this.formFieldData?.options?.pipelines.find(
          (option) => option.pipeline_type === 'static',
        )?.id;
      }

      this.setValues = { pipeline_id: selectedPipeline };
      this._model = { ...this._model, ...this.setValues };
    }

    if (this.initialValue) {
      this._model = { ...this.initialValue };
      this.setValues = { ...(this.setValues ?? {}), ...this.initialValue };
    }
  }

  get queryParams() {
    return this.route.snapshot.queryParams;
  }

  getDrawerDynamicTitle(): string {
    if (this.relatedToType === RelatedToType.Company) return 'Add Company';
    if (this.relatedToType === RelatedToType.Contact) return 'Add Contact';
    if (this.relatedToType === RelatedToType.Project) return 'Add Deal';
    if (this.relatedToType === RelatedToType.Property) return 'Add Property';
    return 'Add';
  }

  getContextPermission(): string | boolean {
    if (this.relatedToType === RelatedToType.Company) return Permission.COMPANY_FORM_EDIT;
    if (this.relatedToType === RelatedToType.Contact) return Permission.CONTACT_FORM_EDIT;
    if (this.relatedToType === RelatedToType.Property) return Permission.PROPERTY_FORM_EDIT;
    if (this.relatedToType === RelatedToType.Project) return Permission.PIPELINE_DEAL_FORM_EDIT;
    return true;
  }

  onFieldUpdate(event: Dictionary<any>): void {
    this._model = { ...this._model, ...event };
  }

  dropped(event: CdkDragDrop<CustomFormField[]>): void {
    moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
  }

  navigateByUrl(url: string): void {
    this.onNavigationByUrlRequested.emit(url);
    this.layoutService.closeComponentDrawer();
  }

  cancel(): void {
    this.layoutService.closeComponentDrawer();
  }

  save(callbackData?: Dictionary<any>): void {
    this.ui.set({ ...this.ui(), isLoading: true, isSaving: true });

    if (this.beforeSaveAction) {
      this.beforeSaveAction(this._model)
        .pipe(
          untilComponentDestroyed(this),
          tap((result) => {
            if (result) {
              this.saveAction(this._model, callbackData);
            }
          }),
        )
        .subscribe(noop);

      return;
    }

    this.saveAction(this._model, callbackData);
  }

  toggleEditing(): void {
    if (this.ui().isEditing && this.isTemplateDirty) {
      this.currentFields = this._selectedFields.map((selectedField: CustomField) =>
        this.toCustomFormField(selectedField),
      );
    }

    this.ui.set({ ...this.ui(), isEditing: !this.ui().isEditing, isAddingField: false });
  }

  saveTemplate(): void {
    const fields = this.normalizeArray(this.currentFields);
    this.onTemplateChanged.emit(fields);
    this._selectedFields = fields;
    this.ui.set({ ...this.ui(), isEditing: !this.ui().isEditing });
  }

  onToggleLoading(): void {
    this.ui.set({ ...this.ui(), isLoading: !this.ui().isLoading });
  }

  removeField(fieldIndex: number): void {
    if (this.currentFields[fieldIndex].type === CustomFormFieldType.Placeholder) {
      this.ui.set({ ...this.ui(), isAddingField: false });
    }

    this.currentFields.splice(fieldIndex, 1);
  }

  addField(): void {
    this.ui.set({ ...this.ui(), isAddingField: true });
    this.currentFields = [
      ...this.currentFields,
      { name: CustomFormFieldType.Placeholder, type: CustomFormFieldType.Placeholder },
    ];
  }

  saveField(fieldIndex: number): void {
    this.currentFields[fieldIndex] = {
      name: this.selectedFieldDef.name,
      type: CustomFormFieldType.FieldDef,
      fieldDef: this.selectedFieldDef,
    };
    this.ui.set({ ...this.ui(), isAddingField: false });
    this.selectedFieldDef = null;
  }

  onFieldSelected(event: MatSelectChange): void {
    this.selectedFieldDef = event.value as FieldDef;
  }

  isSelected(fieldName: string): boolean {
    return this.currentFields.filter(({ name }) => name === fieldName).length > 0;
  }

  onClearFile(): void {
    this._model.file = undefined;
  }

  private normalizeArray(arr: CustomField[]): CustomField[] {
    return arr.map((item) => keepProps(item, ['name', 'isRequired', 'isRemovable']) as CustomField);
  }

  private toCustomFormField(customField: CustomField): CustomFormField {
    const { name: fieldName } = customField;
    if (this.customFormFields.includes(fieldName)) {
      return {
        name: fieldName,
        type: CustomFormFieldType.Custom,
        isRemovable: customField?.isRemovable,
        ...customField,
      };
    } else {
      return {
        name: fieldName,
        type: CustomFormFieldType.FieldDef,
        isRemovable: customField?.isRemovable,
        isRequired: customField?.isRequired,
        fieldDef: this.allowedFields.find(({ name }) => name === fieldName),
        ...customField,
      };
    }
  }

  invokeAbility(): void {
    if (this.ui().isAiLoading) {
      return;
    }

    this.ui.set({ ...this.ui(), isAiLoading: true });

    let fieldNames = this.currentFields.map((curField) => curField.name).filter((field) => field !== 'template_id');
    let inputForFields = [];

    fieldNames.forEach((field) => {
      const search = this._allowedFields.find((allowedField) => allowedField.name === field);
      if (search) {
        inputForFields.push({
          name: search.name ?? '',
          type: search.type ?? '',
          label: search.label ?? '',
          description: search.description ?? '',
        });
      } else if (['cff_add_properties', 'cff_address', 'address_string'].includes(field)) {
        inputForFields.push({ name: 'address_string', type: 'text', label: 'Address' });
        inputForFields.push({ name: 'city', type: 'text', label: 'City' });
        inputForFields.push({ name: 'state', type: 'text', label: 'State' });
        inputForFields.push({ name: 'postal_code', type: 'text', label: 'Zip' });
      }
    });

    this.ethanAbilities
      .useParseOMAbility({
        inputs: {
          url: decodeURIComponent(this.queryParams?.pdf_url),
          list_of_fields: inputForFields,
        },
      })
      .subscribe({
        next: (data) => {
          for (const [field, value] of Object.entries(data?.output)) {
            if (['address_string', 'city', 'state', 'postal_code'].includes(field)) {
              const address = {
                city: data?.output?.city,
                id: null,
                latitude: null,
                longitude: null,
                place_id: null,
                postal_code: data?.output?.postal_code ?? '',
                state: data?.output?.state ?? '',
                street: data?.output?.address_string ?? '',
              };
              this.setValues = { ...this.setValues, address }; // add pipelin-id as default
              this._model = { ...this._model, address };
            } else {
              this.setValues = { ...this.setValues, [field]: value };
              this._model = { ...this._model, [field]: value };
            }
          }
          this.ui.set({ ...this.ui(), isAiLoading: false });
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.ui.set({ ...this.ui(), isAiLoading: false });
        },
      });
  }
}
